import React, { useState } from 'react';

const Surveys = () => {
  const [surveys] = useState([
    {
      id: 1,
      name: 'HAE Patient Quality of Life Survey',
      type: 'survey',
      status: 'Active',
      responses: 145,
      created: '2024-01-15',
      category: 'Quality of Life'
    },
    {
      id: 2,
      name: 'Treatment Satisfaction Assessment',
      type: 'survey',
      status: 'Completed',
      responses: 89,
      created: '2024-03-22',
      category: 'Treatment'
    },
    {
      id: 3,
      name: 'Symptom Severity Tracker',
      type: 'survey',
      status: 'Active',
      responses: 203,
      created: '2024-02-10',
      category: 'Symptoms'
    },
    {
      id: 4,
      name: 'Medication Adherence Survey',
      type: 'survey',
      status: 'Draft',
      responses: 0,
      created: '2024-01-28',
      category: 'Medication'
    },
    {
      id: 5,
      name: 'Healthcare Provider Feedback',
      type: 'survey',
      status: 'Active',
      responses: 67,
      created: '2024-03-18',
      category: 'Provider Feedback'
    },
    {
      id: 6,
      name: 'Emergency Treatment Experience',
      type: 'survey',
      status: 'Completed',
      responses: 134,
      created: '2024-03-20',
      category: 'Emergency Care'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    category: 'Quality of Life',
    description: '',
    questions: []
  });

  const categories = ['all', 'Quality of Life', 'Treatment', 'Symptoms', 'Medication', 'Provider Feedback', 'Emergency Care'];
  const uploadCategories = ['Quality of Life', 'Treatment', 'Symptoms', 'Medication', 'Provider Feedback', 'Emergency Care'];

  const getSurveyIcon = (status) => {
    switch (status) {
      case 'Active':
        return 'fas fa-play-circle text-green-500';
      case 'Completed':
        return 'fas fa-check-circle text-blue-500';
      case 'Draft':
        return 'fas fa-edit text-yellow-500';
      default:
        return 'fas fa-clipboard-list text-gray-400';
    }
  };

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || survey.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addQuestion = () => {
    setUploadForm(prev => ({
      ...prev,
      questions: [...prev.questions, { text: '', type: 'text' }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setUploadForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const removeQuestion = (index) => {
    setUploadForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleUploadFormChange = (e) => {
    const { name, value } = e.target;
    setUploadForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    // Here you would typically handle the survey creation
    console.log('Creating survey:', uploadForm);
    
    // Reset form and close modal
    setUploadForm({
      name: '',
      category: 'Quality of Life',
      description: '',
      questions: []
    });
    setShowUploadModal(false);
    
    // Show success message (you could use a toast notification library)
    alert('Survey created successfully!');
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setUploadForm({
      name: '',
      category: 'Quality of Life',
      description: '',
      questions: []
    });
  };

  const SurveyCard = ({ survey }) => (
    <div className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow duration-200 cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <i className={`${getSurveyIcon(survey.status)} text-2xl`}></i>
        <div className="flex space-x-2">
          <button className="text-neutral hover:text-primary">
            <i className="fas fa-eye"></i>
          </button>
          <button className="text-neutral hover:text-primary">
            <i className="fas fa-edit"></i>
          </button>
          <button className="text-neutral hover:text-red-500">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
      <h3 className="text-sm font-medium text-primary mb-1 truncate">{survey.name}</h3>
      <div className="flex justify-between items-center text-xs text-neutral mb-2">
        <span>{survey.responses} responses</span>
        <span>{survey.created}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="inline-block px-2 py-1 text-xs bg-accent/20 text-primary rounded-full">
          {survey.category}
        </span>
        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
          survey.status === 'Active' ? 'bg-green-100 text-green-800' :
          survey.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {survey.status}
        </span>
      </div>
    </div>
  );

  const SurveyRow = ({ survey }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <i className={`${getSurveyIcon(survey.status)} text-xl mr-3`}></i>
          <span className="text-sm font-medium text-primary">{survey.name}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral">{survey.category}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          survey.status === 'Active' ? 'bg-green-100 text-green-800' :
          survey.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {survey.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral">{survey.responses}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral">{survey.created}</td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex space-x-2 justify-end">
          <button className="text-neutral hover:text-primary">
            <i className="fas fa-eye"></i>
          </button>
          <button className="text-neutral hover:text-primary">
            <i className="fas fa-edit"></i>
          </button>
          <button className="text-neutral hover:text-red-500">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Surveys</h1>
          <p className="text-neutral">Create and manage patient surveys and questionnaires</p>
        </div>
        {/* <button 
          onClick={() => setShowUploadModal(true)}
          className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
        >
          <i className="fas fa-plus mr-2"></i>
          Create Survey
        </button> */}
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-4 items-center flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral"></i>
              <input
                type="text"
                placeholder="Search surveys..."
                className="w-full pl-10 pr-4 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-neutral hover:text-primary'}`}
            >
              <i className="fas fa-th"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary text-white' : 'text-neutral hover:text-primary'}`}
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Survey Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* {filteredSurveys.map(survey => (
            <SurveyCard key={survey.id} survey={survey} />
          ))} */}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Responses</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light">
              {filteredSurveys.map(survey => (
                <SurveyRow key={survey.id} survey={survey} />
              ))}
            </tbody>
          </table> */}
        </div>
      )}

      {/* Empty State */}
      {filteredSurveys.length === 0 && (
        <div className="text-center py-12">
          <i className="fas fa-clipboard-list text-6xl text-neutral mb-4"></i>
          <h3 className="text-lg font-medium text-primary mb-2">No surveys found</h3>
          <p className="text-neutral">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Create Survey Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-primary">Create New Survey</h3>
              <button
                onClick={closeModal}
                className="text-neutral hover:text-red-500 text-2xl"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="p-6">
              {/* Survey Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral mb-2">
                  Survey Title *
                </label>
                <input
                  type="text"
                  name="name"
                  value={uploadForm.name}
                  onChange={handleUploadFormChange}
                  className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter survey title"
                  required
                />
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={uploadForm.category}
                  onChange={handleUploadFormChange}
                  className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  {uploadCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={uploadForm.description}
                  onChange={handleUploadFormChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Optional description of the survey"
                ></textarea>
              </div>

              {/* Questions Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-neutral">
                    Survey Questions
                  </label>
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="px-3 py-1 bg-accent text-primary rounded-lg hover:bg-accent/80 transition-colors duration-200 text-sm flex items-center"
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Add Question
                  </button>
                </div>

                {uploadForm.questions.map((question, index) => (
                  <div key={index} className="mb-3 p-3 border border-light rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-neutral">Question {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeQuestion(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <input
                          type="text"
                          value={question.text}
                          onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                          className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Enter question text"
                        />
                      </div>
                      <div>
                        <select
                          value={question.type}
                          onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        >
                          <option value="text">Text</option>
                          <option value="multiple-choice">Multiple Choice</option>
                          <option value="rating">Rating Scale</option>
                          <option value="yes-no">Yes/No</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                {uploadForm.questions.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-light rounded-lg">
                    <i className="fas fa-question-circle text-3xl text-neutral mb-2"></i>
                    <p className="text-neutral text-sm">No questions added yet</p>
                    <p className="text-neutral text-xs">Click "Add Question" to get started</p>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-light text-neutral rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!uploadForm.name}
                  className="px-4 py-2 bg-primary hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Create Survey
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Surveys; 